// Client-side view of an "identifier" — the email or phone number a shopper types
// to log in, register or check out.
//
// The authority is cn_norm_identifier() in
// wp-content/themes/metro-child/checkout-next/auth.php. This mirrors it so the UI
// can disable a button, NOT so it can decide anything: whatever slips through here
// is still normalised (and possibly refused) server-side. If you change the rules
// in one place, change them in the other.
//
// Greek AND Cypriot numbers are valid on BOTH storefronts (adaptoras.gr and
// adaptoras.cy) — Cypriots order from .gr and Greeks from .cy.
//   GR  10 national digits            6912345678,  2101234567,  +30 691 234 5678
//   CY   8 national digits            99123456,    +357 99 123456,  0035799123456
// A bare 8-digit number has to look like a CY mobile (9…) — otherwise a half-typed
// Greek number would read as Cypriot.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmailish(raw) {
  return EMAIL_RE.test((raw || '').trim());
}

/**
 * @param {string} raw Anything the shopper typed.
 * @returns {{channel: ''|'email'|'phone', valid: boolean}}
 */
export function classifyIdentifier(raw) {
  const v = (raw || '').trim();
  if (!v) return { channel: '', valid: false };
  if (v.indexOf('@') !== -1) return { channel: 'email', valid: EMAIL_RE.test(v) };

  let d = v.replace(/\D/g, '');
  let cc = '';
  if (d.slice(0, 4) === '0030') { d = d.slice(4); cc = '30'; }
  else if (d.slice(0, 5) === '00357') { d = d.slice(5); cc = '357'; }
  else if (d.length === 12 && d.slice(0, 2) === '30') { d = d.slice(2); cc = '30'; }
  else if (d.length === 11 && d.slice(0, 3) === '357') { d = d.slice(3); cc = '357'; }

  if (cc === '357') return { channel: 'phone', valid: /^\d{8}$/.test(d) };
  if (cc === '30') return { channel: 'phone', valid: d.length === 10 };
  return { channel: 'phone', valid: d.length === 10 || /^9\d{7}$/.test(d) };
}

/** Convenience for the widgets: is this something worth submitting at all? */
export function isValidIdentifier(raw) {
  return classifyIdentifier(raw).valid;
}

/**
 * Is this a contact phone we could reach the customer on? Mirror of
 * cn_valid_contact_phone() in checkout-next/data.php — that one decides whether the
 * order is accepted, this one only decides whether the field looks happy.
 *
 * Ten digits or more always passes (GR national, or anything with a country code).
 * The bare 8-digit Cypriot form (9… mobile, 2… landline) passes only on the CY
 * storefront: on .gr a truncated Greek landline looks identical, and an undialable
 * number on a parcel is worse than asking a Cypriot for +357.
 *
 * @param {string} raw
 * @param {'GR'|'CY'} country Storefront country.
 */
export function isValidContactPhone(raw, country) {
  const d = (raw || '').replace(/\D/g, '');
  if (d.length >= 10) return true;
  return country === 'CY' && /^[29]\d{7}$/.test(d);
}
